<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-xl bg-gray-800 text-gray-300 rounded-lg shadow-lg border border-gray-700 animate-scaleIn flex flex-col gap-4 relative"
         style="max-height:90vh;">
      
      <!-- Header -->
      <div class="flex items-center justify-between px-4 pt-4">
        <div class="flex items-center gap-3">
          <img
            :src="data?.img || defaultImg"
            alt="Mechanical Organ Image"
            class="w-9 h-9 sm:w-14 sm:h-14 rounded-md object-cover border-2"
            :class="data?.rarityColors?.borderColor || 'border-gray-600'"
            @error="handleImageError"
          />
          <div>
            <h1 class="text-base sm:text-lg font-semibold"
                :class="data?.rarityColors?.textColor || 'text-gray-300'">
              {{ data?.name || 'ไม่ระบุ' }}
            </h1>
            <p class="text-gray-300 text-xs sm:text-sm">ประเภท: อวัยวะจักรกล</p>
            <div v-if="data?.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="star in Number(data.rarity)" :key="star">
                <i class="fas fa-star text-yellow-400 text-xs sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>
        <button
          @click="$emit('close')"
          class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group"
          :class="{ 'opacity-50 cursor-not-allowed': isUpgrading || isResetting }"
          aria-label="ปิด"
          :disabled="isUpgrading || isResetting"
        >
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Tabs -->
      <div class="px-4">
        <div class="grid grid-cols-3 border-b border-gray-700 bg-gray-800 rounded-t-md">
          <button
            @click="activeTab = 'info'"
            :class="[
              'relative px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200',
              activeTab === 'info'
                ? 'text-indigo-300 border-b-2 border-indigo-600'
                : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-900'
            ]"
            :aria-selected="activeTab === 'info'"
            aria-label="ข้อมูล"
          >
            ข้อมูล
            <span v-if="activeTab === 'info'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
          </button>
          <button
            @click="activeTab = 'upgrade'"
            :class="[
              'relative px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200',
              activeTab === 'upgrade'
                ? 'text-indigo-300 border-b-2 border-indigo-600'
                : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-900'
            ]"
            :aria-selected="activeTab === 'upgrade'"
            aria-label="อัปเกรด"
            :disabled="currentLevel >= 20 && !isReallocate"
          >
            อัปเกรด
            <span v-if="activeTab === 'upgrade'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
          </button>
          <button
            @click="activeTab = 'reset'"
            :class="[
              'relative px-3 py-2 text-xs sm:text-sm font-medium transition-colors duration-200',
              activeTab === 'reset'
                ? 'text-indigo-300 border-b-2 border-indigo-600'
                : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-900'
            ]"
            :aria-selected="activeTab === 'reset'"
            aria-label="รีเซ็ต"
            :disabled="data.upgradeCount === 0"
          >
            รีเซ็ต
            <span v-if="activeTab === 'reset'" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
          </button>
        </div>
      </div>

      <!-- Notification -->
      <div v-if="notificationMessage" class="px-4 -mt-2">
        <div class="text-center text-xs sm:text-sm"
             :class="notificationType === 'success' ? 'text-green-400' : 'text-red-300'">
          {{ notificationMessage }}
        </div>
      </div>

      <!-- Content -->
      <div class="px-4 pb-4 space-y-4 overflow-y-auto scrollbar-thin" style="max-height: calc(90vh - 170px);">
        <!-- Info Tab -->
        <div v-if="activeTab === 'info'" class="space-y-4 animate-fadeIn">
          <!-- Stats -->
          <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
            <h2 class="text-sm font-semibold text-gray-100 mb-2">สถานะปัจจุบัน</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div v-for="key in principalStatsKeys" :key="key" class="flex items-center gap-2">
                <i :class="['text-indigo-400 text-sm', statIcons[key]]"></i>
                <span class="text-gray-300">
                  {{ key.charAt(0).toUpperCase() + key.slice(1) }}:
                  <strong class="text-gray-100">{{ calculatedStats[key] }}</strong>
                </span>
              </div>
            </div>
          </section>

          <!-- Upgrade Availability -->
          <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
            <h2 class="text-sm font-semibold text-gray-100 mb-2">สถานะการอัปเกรด</h2>
          </section>
        </div>

        <!-- Upgrade Tab -->
        <div v-if="activeTab === 'upgrade'" class="space-y-4 animate-fadeIn">
          <div v-if="currentLevel >= 20 && !isReallocate" class="text-center text-yellow-400 py-4 text-xs sm:text-sm">
            ถึงขีดจำกัดการอัปเกรดแล้ว
          </div>

          <div v-else class="space-y-4 h-72 overflow-auto" id="style-1">
            <!-- Point Distributor -->
            <section class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <h2 class="text-sm font-semibold text-gray-100 mb-3">
                กระจาย {{ requiredPoints }} แต้ม
              </h2>
              <div class="grid grid-cols-2 gap-2">
                <div
                  v-for="stat in allowedStats"
                  :key="stat"
                  class="bg-gray-800 border border-gray-700 rounded-md p-2 flex flex-col items-center"
                >
                  <i :class="['text-indigo-400 mb-1 text-sm', statIcons[stat]]"></i>
                  <span class="text-gray-300 text-xs mb-1 capitalize">{{ stat }}</span>
                  <div class="flex items-center gap-1 w-full">
                    <button
                      @click="decreasePoint(stat)"
                      class="flex-1 bg-gray-800 border border-gray-700 py-1 rounded hover:bg-gray-900 text-sm transition-colors duration-200"
                    >-</button>
                    <template v-if="isReallocate">
                      <input
                        type="number"
                        v-model.number="points[stat]"
                        min="0"
                        step="1"
                        class="w-10 text-center bg-gray-800 border border-gray-700 py-1 rounded text-sm text-gray-300"
                        @change="clampPoint(stat)"
                      />
                    </template>
                    <template v-else>
                      <span class="w-10 text-center bg-gray-800 border border-gray-700 py-1 rounded text-sm text-gray-300">
                        {{ points[stat] || 0 }}
                      </span>
                    </template>
                    <button
                      @click="increasePoint(stat)"
                      class="flex-1 bg-gray-800 border border-gray-700 py-1 rounded hover:bg-gray-900 text-sm transition-colors duration-200"
                    >+</button>
                  </div>
                </div>
              </div>
              <p v-if="totalPoints !== requiredPoints" class="text-red-300 text-xs mt-2 text-center">ต้องกระจายครบ {{ requiredPoints }} แต้ม</p>
            </section>

            <!-- Materials (only if not reallocate) -->
            <section v-if="!isReallocate" class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <h3 class="text-sm font-semibold text-gray-100 mb-2">
                ไอเทมที่ต้องใช้สำหรับอัปเกรดจากระดับ {{ currentLevel }} ไป {{ currentLevel + 1 }}
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div
                  v-for="(material, index) in currentMaterials"
                  :key="index"
                  class="flex items-center justify-between text-xs sm:text-sm bg-gray-800 border rounded-md p-2"
                  :class="hasEnoughMaterial(material) ? 'border-green-700' : 'border-red-700'"
                >
                  <span class="text-gray-300 truncate pr-2">{{ material.itemName }}</span>
                  <span :class="hasEnoughMaterial(material) ? 'text-green-400' : 'text-red-400'">
                    {{ getMaterialCount(material.itemName) }} / {{ material.quantity }}
                  </span>
                </div>
              </div>
            </section>

            <!-- Pity Selection (only for rarity >= 10) -->
            <section v-if="!isReallocate && data.rarity >= 10" class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <h2 class="text-sm font-semibold text-gray-100 mb-2">ระบบการันตี (สิทธิ์สะสม: {{ pityCount }})</h2>
              <div class="text-xs sm:text-sm text-gray-300">
                <p>ใช้สิทธิ์:
                  <select v-model="pitiesToUse" class="bg-gray-700 text-gray-300 border border-gray-600 rounded-md px-2 py-1">
                    <option v-for="n in (maxUsablePities + 1)" :key="n-1" :value="n-1">{{ n-1 }}</option>
                  </select>
                </p>
                <p>โบนัสจากสิทธิ์: <strong class="text-green-400">+{{ pityBonus }}%</strong></p>
              </div>
            </section>

            <!-- Success Chance (only if not reallocate) -->
            <section v-if="!isReallocate" class="bg-gray-800 border border-gray-700 rounded-md p-3">
              <h2 class="text-sm font-semibold text-gray-100 mb-2">โอกาสสำเร็จ</h2>
              <div class="text-xs sm:text-sm text-gray-300">
                <p>ระดับ {{ currentLevel }} → {{ currentLevel + 1 }}</p>
                <p>โอกาสพื้นฐาน: <strong class="text-gray-100">{{ baseChance }}%</strong></p>
                <p v-if="data.rarity >= 10 && pitiesToUse > 0">
                  โบนัสจากสิทธิ์: <strong class="text-green-400">+{{ pityBonus }}%</strong>
                </p>
                <p>โอกาสรวม: <strong class="text-gray-100">{{ currentChance }}%</strong></p>
              </div>
            </section>

            <!-- Protect Option (only if not reallocate) -->
            <section v-if="!isReallocate" class="bg-gray-800 border border-gray-700 rounded-md p-3 hover:border-indigo-600 transition-colors duration-200">
              <div class="flex items-center justify-between gap-3">
                <span class="text-gray-300 text-xs sm:text-sm">
                  ป้องกันการสูญเสียไอเท็ม
                  <span class="text-gray-100">(เสีย {{ protectDiamond }} <i class="fa-solid fa-gem text-blue-400"></i> และ {{ protectSilver }} <i class="fa-solid fa-coins text-gray-200"></i>)</span>
                </span>
                <label class="switch">
                  <input type="checkbox" v-model="protect" />
                  <span class="slider round"></span>
                </label>
              </div>
              <p v-if="protect && !hasEnoughResources" class="text-red-300 text-xs mt-2 text-center animate-fadeIn">
                ทรัพยากรไม่เพียงพอ
              </p>
            </section>

            <!-- Progress / CTA -->
            <section>
              <!-- Suspense Progress -->
              <div v-if="isUpgrading" class="space-y-2">
                <div class="bg-gray-800 h-4 rounded overflow-hidden relative border border-gray-700">
                  <div
                    ref="progressBar"
                    :style="{ width: progress + '%' }"
                    class="h-4 transition-all duration-300 ease-out bg-gradient-to-r from-indigo-600 to-indigo-400 animate-pulse-glow"
                  ></div>
                  <div class="absolute inset-0 flex items-center justify-center text-xs text-white font-semibold">
                    {{ suspenseText }}
                  </div>
                </div>
                <p class="text-center text-gray-300 text-xs animate-pulse">กำลังอัปเกรด... ลุ้นผลลัพธ์!</p>
              </div>

              <!-- Upgrade Button -->
              <button
                v-else
                @click="upgradeMechanical"
                class="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm py-2 rounded-md transition-colors duration-200 border border-purple-700 flex items-center justify-center gap-2"
                :disabled="totalPoints !== requiredPoints || (!isReallocate && (!hasEnoughMaterials || (protect && !hasEnoughResources) || currentChance > 80))"
              >
                <i class="fas fa-arrow-up text-xs"></i>
                <span>{{ isReallocate ? 'ยืนยันการกระจาย' : 'อัปเกรด' }}</span>
              </button>
            </section>
          </div>
        </div>

        <!-- Reset Tab -->
        <div v-if="activeTab === 'reset'" class="space-y-4 animate-fadeIn">
          <div v-if="data.upgradeCount === 0" class="text-center text-yellow-400 py-4 text-xs sm:text-sm">
            ยังไม่สามารถรีเซ็ตได้ (ต้องอัปเกรดอย่างน้อย 1 ครั้ง)
          </div>
          <section v-else class="bg-gray-800 border border-gray-700 rounded-md p-3">
            <h2 class="text-sm font-semibold text-gray-100 mb-2">รีเซ็ตสเตตัส</h2>
            <p class="text-xs sm:text-sm text-gray-300">คุณจะได้รับแต้มคืน: <strong>{{ pendingResetPoints }}</strong> แต้ม เพื่อกระจายใหม่</p>
            <h3 class="text-sm font-semibold text-gray-100 mt-3 mb-2">ไอเทมที่ต้องใช้</h3>
            <div class="grid grid-cols-1 gap-2">
              <div class="flex items-center justify-between text-xs sm:text-sm bg-gray-800 border rounded-md p-2"
                   :class="hasResetCard ? 'border-green-700' : 'border-red-700'">
                <span class="text-gray-300">การ์ดรีเซ็ตข้อมูล</span>
                <span :class="hasResetCard ? 'text-green-400' : 'text-red-400'">{{ getMaterialCount('การ์ดรีเซ็ตข้อมูล') }} / 1</span>
              </div>
            </div>
            <!-- Suspense Progress for Reset -->
            <div v-if="isResetting" class="space-y-2 mt-3">
              <div class="bg-gray-800 h-4 rounded overflow-hidden relative border border-gray-700">
                <div
                  ref="progressBarReset"
                  :style="{ width: progressReset + '%' }"
                  class="h-4 transition-all duration-300 ease-out bg-gradient-to-r from-indigo-600 to-indigo-400 animate-pulse-glow"
                ></div>
                <div class="absolute inset-0 flex items-center justify-center text-xs text-white font-semibold">
                  {{ suspenseTextReset }}
                </div>
              </div>
              <p class="text-center text-gray-300 text-xs animate-pulse">กำลังรีเซ็ต... ลุ้นผลลัพธ์!</p>
            </div>
            <button
              v-else
              @click="resetMechanical"
              class="w-full bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm py-2 rounded-md transition-colors duration-200 mt-3"
              :disabled="!hasResetCard"
            >
              <i class="fas fa-undo text-xs"></i> รีเซ็ตสเตตัส
            </button>
          </section>
        </div>
      </div>

      <!-- Footer (Close) -->
      <div class="px-4 pb-4">
        <button
          @click="$emit('close')"
          class="w-full bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm flex items-center justify-center gap-2"
          :class="{ 'opacity-50 cursor-not-allowed': isUpgrading || isResetting }"
          :disabled="isUpgrading || isResetting"
        >
          <i class="fas fa-times text-xs"></i> ปิด
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFunctions, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import defaultImg from '@/assets/default-item.png';

export default {
  name: 'MechanicalUpgradeModal',
  props: {
    itemId: {
      type: String,
      required: true,
    },
    inventoryItems: {
      type: Array,
      required: true,
    },
  },
  emits: ['close', 'mechanical-upgraded', 'mechanical-reset'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const activeTab = ref('info');
    const points = ref({});
    const isUpgrading = ref(false);
    const notificationMessage = ref('');
    const notificationType = ref('success');
    const data = ref({});
    const character = ref({});
    const progress = ref(0);
    const progressBar = ref(null);
    const suspenseText = ref('กำลังประมวลผล...');
    const protect = ref(false);
    const isResetting = ref(false);
    const progressReset = ref(0);
    const progressBarReset = ref(null);
    const suspenseTextReset = ref('กำลังประมวลผล...');
    const pityCount = ref(0);
    const pitiesToUse = ref(0);
    let unsubscribeItem = null;
    let unsubscribePity = null;

    watch(() => playerStore.characterData, (data) => {
      if (data) character.value = data;
    }, { immediate: true });

    const statIcons = {
      stamina: 'fas fa-heart',
      str: 'fas fa-dumbbell',
      int: 'fas fa-brain',
      agi: 'fas fa-running',
    };

    const principalStatsKeys = ['stamina', 'str', 'int', 'agi'];

    const allowedStats = computed(() => (data.value.upgradeableStats || []).filter(stat => principalStatsKeys.includes(stat)));

    const remainingUpgrades = computed(() => (data.value.maxUpgradeCount || 20) - (data.value.upgradeCount || 0));

    const currentLevel = computed(() => data.value.rarity || 1);

    const baseChance = computed(() => {
      const rarity = currentLevel.value;
      if (rarity >= 1 && rarity <= 3) return 80 - (rarity - 1) * 5;
      if (rarity >= 4 && rarity <= 6) return 65 - (rarity - 4) * 5;
      if (rarity >= 7 && rarity <= 9) return 50 - (rarity - 7) * 5;
      if (rarity === 10 || rarity === 11) return 80;
      if (rarity === 12) return 75;
      if (rarity === 13) return 70;
      if (rarity === 14) return 65;
      if (rarity === 15) return 60;
      if (rarity === 16) return 55;
      if (rarity === 17) return 50;
      if (rarity === 18) return 45;
      if (rarity === 19) return 40;
      return 100;
    });

    const pityBonus = computed(() => {
      if (data.value.rarity < 10) return 0;
      // ทุกสิทธิ์เพิ่ม 10%
      return pitiesToUse.value * 10;
    });

    const currentChance = computed(() => {
      return baseChance.value + pityBonus.value;
    });

    const maxUsablePities = computed(() => {
      if (data.value.rarity < 10) return 0;
      
      const maxChance = 80;
      const remainingChance = maxChance - baseChance.value;
      
      if (remainingChance <= 0) return 0;
      
      // คำนวณจำนวนสิทธิ์ที่ต้องการเพื่อให้ถึง 80%
      // ทุกสิทธิ์เพิ่ม 10% ดังนั้น จำนวนสิทธิ์ = Math.ceil(remainingChance / 10)
      const neededPities = Math.floor(remainingChance / 10);
      
      // คืนค่าน้อยที่สุดระหว่าง: จำนวนที่คำนวณได้ กับ จำนวนสิทธิ์ที่มี
      return Math.min(neededPities, pityCount.value);
    });

    const currentMaterials = computed(() => {
  const rarity = currentLevel.value;
  if (rarity >= 1 && rarity <= 3) {
    return [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 1 }];
  } else if (rarity >= 4 && rarity <= 6) {
    return [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 2 }];
  } else if (rarity >= 7 && rarity <= 9) {
    return [{ itemName: 'การ์ดประมวลผลเสริม', quantity: 3 }];
  } else if (rarity >= 10 && rarity <= 12) {
    return [
      { itemName: 'การ์ดประมวลผลเสริม', quantity: 4 },
      { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 1 },
    ];
  } else if (rarity === 13) {
    return [
      { itemName: 'การ์ดประมวลผลเสริม', quantity: 5 },
      { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 2 },
    ];
  } else if (rarity >= 14 && rarity <= 15) {
    return [
      { itemName: 'การ์ดประมวลผลเสริม', quantity: 5 },
      { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 2 },
    ];
  } else if (rarity === 16) {
    return [
      { itemName: 'การ์ดประมวลผลเสริม', quantity: 6 },
      { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 3 },
    ];
  } else if (rarity >= 17 && rarity <= 18) {
    return [
      { itemName: 'การ์ดประมวลผลเสริม', quantity: 6 },
      { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 3 },
    ];
  } else if (rarity === 19) {
    return [
      { itemName: 'การ์ดประมวลผลเสริม', quantity: 7 },
      { itemName: 'การ์ดประมวลผลเสริมขั้นกลาง', quantity: 4 },
    ];
  }
  return [];
});

    const calculatedStats = computed(() => {
      const baseStats = data.value.stats || {};
      const upgradeStats = data.value.upgradeStats || {};
      return {
        stamina: (baseStats.stamina || 0) + (upgradeStats.stamina || 0),
        str: (baseStats.str || 0) + (upgradeStats.str || 0),
        int: (baseStats.int || 0) + (upgradeStats.int || 0),
        agi: (baseStats.agi || 0) + (upgradeStats.agi || 0),
      };
    });

    const totalPoints = computed(() => Object.values(points.value).reduce((sum, val) => sum + (val || 0), 0));

    const materialCounts = computed(() => {
      const counts = {};
      props.inventoryItems.forEach((invItem) => {
        counts[invItem.name] = (counts[invItem.name] || 0) + (invItem.quantity || 1);
      });
      return counts;
    });

    const getMaterialCount = (itemName) => materialCounts.value[itemName] || 0;

    const hasEnoughMaterial = (material) => getMaterialCount(material.itemName) >= material.quantity;

    const hasEnoughMaterials = computed(() => {
      return currentMaterials.value.every(hasEnoughMaterial);
    });

    const currentDiamond = computed(() => character.value.diamond || 0);
    const currentSilver = computed(() => character.value.silver || 0);

    const protectCosts = {
      1: { diamond: 200, silver: 20000 },
      2: { diamond: 300, silver: 30000 },
      3: { diamond: 400, silver: 40000 },
      4: { diamond: 500, silver: 50000 },
      5: { diamond: 600, silver: 60000 },
      6: { diamond: 700, silver: 70000 },
      7: { diamond: 800, silver: 80000 },
      8: { diamond: 900, silver: 90000 },
      9: { diamond: 1000, silver: 100000 },
      10: { diamond: 1100, silver: 110000 },
      11: { diamond: 1200, silver: 120000 },
      12: { diamond: 1300, silver: 130000 },
      13: { diamond: 1400, silver: 140000 },
      14: { diamond: 1500, silver: 150000 },
      15: { diamond: 1600, silver: 160000 },
      16: { diamond: 1700, silver: 170000 },
      17: { diamond: 1800, silver: 180000 },
      18: { diamond: 1900, silver: 190000 },
      19: { diamond: 2000, silver: 200000 },
    };

    const protectDiamond = computed(() => protectCosts[currentLevel.value]?.diamond || 0);
    const protectSilver = computed(() => protectCosts[currentLevel.value]?.silver || 0);

    const hasEnoughResources = computed(() => currentDiamond.value >= protectDiamond.value && currentSilver.value >= protectSilver.value);

    const isReallocate = computed(() => (data.value.statPoints || 0) > 0);

    const requiredPoints = computed(() => isReallocate.value ? data.value.statPoints : 3);

    const pendingResetPoints = computed(() => principalStatsKeys.reduce((sum, stat) => sum + (data.value.upgradeStats?.[stat] || 0), 0));

    const hasResetCard = computed(() => getMaterialCount('การ์ดรีเซ็ตข้อมูล') >= 1);

    const handleImageError = (event) => {
      event.target.src = defaultImg;
    };

    const increasePoint = (stat) => {
      if (totalPoints.value < requiredPoints.value) {
        points.value[stat] = (points.value[stat] || 0) + 1;
      }
    };

    const decreasePoint = (stat) => {
      if (points.value[stat] > 0) {
        points.value[stat]--;
      }
    };

    const clampPoint = (stat) => {
      if (points.value[stat] < 0 || isNaN(points.value[stat])) {
        points.value[stat] = 0;
      }
    };

    const showNotification = (message, type = 'success') => {
      notificationMessage.value = message;
      notificationType.value = type;
      setTimeout(() => {
        notificationMessage.value = '';
      }, 3000);
    };

    const animateProgress = (isReset = false) => {
      const prog = isReset ? progressReset : progress;
      const suspText = isReset ? suspenseTextReset : suspenseText;
      const progBar = isReset ? progressBarReset : progressBar;
      return new Promise(resolve => {
        let increment = 0;
        let speed = 10; // Initial speed
        const suspenseMessages = [
          'กำลังประมวลผล...',
          'ตรวจสอบวัตถุดิบ...',
          'ปรับแต่งพลังงาน...',
          'เกือบเสร็จแล้ว...',
          'ลุ้นเลย!',
        ];
        let messageIndex = 0;

        const interval = setInterval(() => {
          if (Math.random() < 0.2 && increment > 20 && increment < 80) {
            speed = 0;
            suspText.value = suspenseMessages[Math.min(messageIndex, suspenseMessages.length - 1)];
            setTimeout(() => {
              speed = Math.random() * 15 + 5;
            }, 500);
          } else {
            increment += speed;
            speed = Math.max(5, speed * 0.95);
            prog.value = Math.min(increment, 100);
            suspText.value = suspenseMessages[Math.min(messageIndex, suspenseMessages.length - 1)];
          }

          if (increment >= 100) {
            clearInterval(interval);
            suspText.value = 'เสร็จสิ้น!';
            resolve();
          }

          messageIndex = Math.floor(increment / 25);
        }, 100);
      });
    };

    const upgradeMechanical = async () => {
      if (totalPoints.value !== requiredPoints.value) return;
      if (!isReallocate.value && (!hasEnoughMaterials.value || (protect.value && !hasEnoughResources.value))) return;
      if (!isReallocate.value && currentChance.value > 80) {
        showNotification('โอกาสรวมเกิน 80% ไม่สามารถอัปเกรดได้', 'error');
        return;
      }
      isUpgrading.value = true;
      progress.value = 0;
      suspenseText.value = 'กำลังประมวลผล...';

      try {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const animationPromise = animateProgress();

        const gameActions = projectFunctions.httpsCallable('gameActions');
        let callData;
        if (isReallocate.value) {
          callData = {
            action: 'mechanicalOrganAction',
            subAction: 'reallocate',
            instanceId: props.itemId,
            points: points.value,
          };
        } else {
          callData = {
            action: 'mechanicalOrganAction',
            subAction: 'upgrade',
            instanceId: props.itemId,
            points: points.value,
            protect: protect.value,
            pitiesToUse: data.value.rarity >= 10 ? pitiesToUse.value : 0,
          };
        }
        const result = await gameActions(callData);
        const { success, message, updatedItem } = result.data;

        await animationPromise;

        if (progressBar.value) {
          progressBar.value.classList.remove('bg-gradient-to-r', 'from-indigo-600', 'to-indigo-400', 'animate-pulse-glow');
          progressBar.value.classList.add(success ? 'bg-green-600' : 'bg-red-600');
          if (!success) {
            progressBar.value.classList.add('animate-shake');
          }
        }

        await new Promise(resolve => setTimeout(resolve, 1000));

        if (success) {
          showNotification(isReallocate.value ? 'กระจายแต้มสำเร็จ!' : 'อัปเกรดสำเร็จ!', 'success');
          if (updatedItem) {
            data.value = { ...data.value, ...updatedItem, ...(updatedItem.states || {}) };
          }
          emit('mechanical-upgraded', updatedItem);
          points.value = allowedStats.value.reduce((acc, stat) => ({ ...acc, [stat]: 0 }), {});
          pitiesToUse.value = 0;
          // Do not update pityCount locally; rely on Firestore snapshot
        } else {
          showNotification(message || (isReallocate.value ? 'กระจายแต้มล้มเหลว' : 'อัปเกรดล้มเหลว'), 'error');
          points.value = allowedStats.value.reduce((acc, stat) => ({ ...acc, [stat]: 0 }), {});
          pitiesToUse.value = 0;
          // Do not update pityCount locally; rely on Firestore snapshot
        }
      } catch (err) {
        console.error('Error upgrading mechanical organ:', err);
        showNotification('เกิดข้อผิดพลาดในการอัปเกรด', 'error');
      } finally {
        isUpgrading.value = false;
        if (progressBar.value) {
          progressBar.value.classList.remove('animate-shake');
        }
      }
    };

    const resetMechanical = async () => {
      if (!hasResetCard.value) return;
      isResetting.value = true;
      progressReset.value = 0;
      suspenseTextReset.value = 'กำลังประมวลผล...';

      try {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const animationPromise = animateProgress(true);

        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'mechanicalOrganAction', subAction: 'reset', instanceId: props.itemId });
        const { success, message, updatedItem } = result.data;

        await animationPromise;

        if (progressBarReset.value) {
          progressBarReset.value.classList.remove('bg-gradient-to-r', 'from-indigo-600', 'to-indigo-400', 'animate-pulse-glow');
          progressBarReset.value.classList.add(success ? 'bg-green-600' : 'bg-red-600');
          if (!success) {
            progressBarReset.value.classList.add('animate-shake');
          }
        }

        await new Promise(resolve => setTimeout(resolve, 1000));

        if (success) {
          showNotification('รีเซ็ตสเตตัสสำเร็จ! คุณได้รับแต้มคืน', 'success');
          if (updatedItem) {
            data.value = { ...data.value, ...updatedItem, ...(updatedItem.states || {}) };
          }
          emit('mechanical-reset', updatedItem);
          activeTab.value = 'upgrade';
        } else {
          showNotification(message || 'รีเซ็ตล้มเหลว', 'error');
        }
      } catch (err) {
        showNotification('เกิดข้อผิดพลาดในการรีเซ็ต', 'error');
      } finally {
        isResetting.value = false;
        if (progressBarReset.value) {
          progressBarReset.value.classList.remove('animate-shake');
        }
      }
    };

    watch([pityCount], () => {
      if (pitiesToUse.value > maxUsablePities.value) {
        pitiesToUse.value = maxUsablePities.value;
      }
      if (pitiesToUse.value > pityCount.value) {
        pitiesToUse.value = pityCount.value;
      }
    });

    // Watch for prop inventoryItems changes (e.g. Firestore onSnapshot updates)
    watch(() => props.inventoryItems, (newItems) => {
      if (!newItems || !Array.isArray(newItems)) return;
      const found = newItems.find(item => item.id === props.itemId || item.instanceId === props.itemId);
      if (found) {
        data.value = found;
      }
    }, { deep: true });

    const subscribeToPity = () => {
      if (data.value.rarity >= 10) {
        const pityDocId = `${user.value.uid}_${data.value.name}_${currentLevel.value}`;
        unsubscribePity = projectFirestore.collection('upgradePities').doc(pityDocId).onSnapshot((pitySnap) => {
          pityCount.value = pitySnap.exists ? (pitySnap.data().pityCount || 0) : 0;
        });
      }
    };

    onMounted(() => {
      allowedStats.value.forEach(stat => {
        points.value[stat] = 0;
      });

      // Find item from inventoryItems (new object system) instead of querying 'items' collection
      const findItem = () => {
        const found = props.inventoryItems.find(item => item.id === props.itemId || item.instanceId === props.itemId);
        if (found) {
          data.value = found;
          allowedStats.value.forEach(stat => {
            if (!(stat in points.value)) points.value[stat] = 0;
          });
          // Subscribe to pity data for rarity >= 10
          if (unsubscribePity) unsubscribePity();
          subscribeToPity();
        } else {
          showNotification('ไม่พบไอเทมในกระเป๋า', 'error');
          emit('close');
        }
      };
      findItem();
    });

    onUnmounted(() => {
      if (unsubscribeItem) unsubscribeItem();
      if (unsubscribePity) unsubscribePity();
    });

    return {
      activeTab,
      points,
      isUpgrading,
      notificationMessage,
      notificationType,
      data,
      allowedStats,
      remainingUpgrades,
      currentLevel,
      baseChance,
      pityBonus,
      currentChance,
      maxUsablePities,
      pityCount,
      pitiesToUse,
      calculatedStats,
      totalPoints,
      statIcons,
      handleImageError,
      increasePoint,
      decreasePoint,
      clampPoint,
      showNotification,
      upgradeMechanical,
      currentMaterials,
      hasEnoughMaterials,
      hasEnoughMaterial,
      getMaterialCount,
      principalStatsKeys,
      progress,
      progressBar,
      suspenseText,
      protect,
      protectDiamond,
      protectSilver,
      hasEnoughResources,
      isReallocate,
      requiredPoints,
      pendingResetPoints,
      hasResetCard,
      resetMechanical,
      isResetting,
      progressReset,
      progressBarReset,
      suspenseTextReset,
      defaultImg,
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
.animate-pulse {
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.animate-shake {
  animation: shake 0.5s;
}

@keyframes shake {
  0% { transform: translateX(0); }
  10% { transform: translateX(-10px); }
  20% { transform: translateX(10px); }
  30% { transform: translateX(-10px); }
  40% { transform: translateX(10px); }
  50% { transform: translateX(-10px); }
  60% { transform: translateX(10px); }
  70% { transform: translateX(-10px); }
  80% { transform: translateX(10px); }
  90% { transform: translateX(-10px); }
  100% { transform: translateX(0); }
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes pulse-glow {
  0% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.5); }
  50% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.8); }
  100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.5); }
}
.animate-pulse-glow {
  animation: pulse-glow 1.5s infinite;
}

@keyframes tabSlide {
  from { width: 0; }
  to { width: 100%; }
}
.animate-tabSlide {
  animation: tabSlide 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

/* Game-Style Toggle Switch with Neon Glow */
.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 28px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #4a5568;
  transition: .4s ease-in-out;
  border-radius: 28px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.5);
}

.slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: #e5e7eb;
  transition: .4s ease-in-out;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

input:checked + .slider {
  background-color: #22c55e;
  box-shadow: 0 0 10px #22c55e, 0 0 20px #22c55e, 0 0 30px rgba(34, 197, 94, 0.5);
}

input:checked + .slider:before {
  transform: translateX(22px);
  background-color: #ffffff;
}

.switch:hover .slider {
  box-shadow: 0 0 5px rgba(99, 102, 241, 0.5);
}
</style>
